import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import StatusPill from './StatusPill'
import { supabase } from '../lib/supabase'
import {
  canEditExtraServiceLine,
  canSendExtraServiceLineApproval,
  extraServiceLineBillableTotal,
  extraServiceLineModuleLabel,
  extraServiceLineNeedsCustomerExplanation,
  extraServiceLinePrice,
  normaliseExtraServiceLineModuleKey,
  extraServiceLineStatusLabel,
  extraServiceLineStatusTone,
  isMissingExtraServiceLineSchemaError,
  serviceLineSnapshotName,
} from '../lib/generalExtraServiceLines'
import {
  formatServiceMoney,
  serviceBookingLabel,
  serviceCategoryLabel,
  servicePriceLabel,
  servicePricingMode,
} from '../lib/serviceMenu'

function text(value) {
  return String(value || '').trim()
}

function fieldAmount(value) {
  const number = Number(value)
  return Number.isFinite(number) && number > 0 ? number.toFixed(2) : ''
}

function displayDateTime(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
}

function evidenceTitle(item = {}) {
  return item.caption || item.file_name || 'Evidence'
}

function serviceSnapshot(service = {}, moduleKey = 'general') {
  const normalisedModuleKey = normaliseExtraServiceLineModuleKey(moduleKey)
  return {
    id: service.id,
    stable_service_id: service.id,
    module_key: normalisedModuleKey,
    name: service.name || '',
    category: service.category || '',
    component: normalisedModuleKey === 'suspension' ? service.category || '' : '',
    category_label: serviceCategoryLabel(service),
    description: service.description || '',
    base_price: Number(service.base_price || 0),
    pricing_mode: servicePricingMode(service),
    booking_type: service.booking_type || 'direct_bookable',
    expected_labour_minutes: Number(service.expected_labour_minutes || 0),
    price_label: servicePriceLabel(service),
    booking_label: serviceBookingLabel(service),
  }
}

function makeEmptyForm(source = {}) {
  return {
    id: '',
    source_type: source.source_type || 'manual',
    checklist_item_id: source.checklist_item_id || null,
    finding_id: source.finding_id || null,
    service_id: '',
    customer_explanation: source.customer_explanation || '',
    internal_note: source.internal_note || '',
    line_amount: '',
    approval_required: true,
    customer_visible: true,
    status: 'draft',
    technician_completion_comment: '',
  }
}

function formFromRequest(row = {}) {
  return {
    id: row.id || '',
    source_type: row.source_type || 'manual',
    checklist_item_id: row.checklist_item_id || null,
    finding_id: row.finding_id || null,
    service_id: row.service_id || row.service_snapshot?.id || '',
    customer_explanation: row.customer_explanation || '',
    internal_note: row.internal_note || '',
    line_amount: fieldAmount(row.line_amount || row.service_snapshot?.base_price),
    approval_required: row.approval_required !== false,
    customer_visible: row.customer_visible !== false,
    status: row.status || 'draft',
    technician_completion_comment: row.technician_completion_comment || '',
  }
}

function sourceLabel(row = {}) {
  if (row.source_type === 'checklist_item') return 'Checklist item'
  if (row.source_type === 'finding') return 'Finding'
  return 'Manual'
}

export default function GeneralExtraServiceLinePanel({
  job,
  workshopId,
  services = [],
  mode = 'edit',
  moduleKey = 'general',
  title = 'Extra Service Lines',
  introTitle,
  introText,
  serviceSelectLabel,
  approvalEndpoint,
  savedBeforeAddingMessage,
  emptyMessage,
  sourceDraft = null,
  refreshKey = 0,
  customerEmail = '',
  onSourceConsumed,
  onChanged,
  onTotalsChange,
  onRowsChange,
}) {
  const editable = mode === 'edit'
  const normalisedModuleKey = normaliseExtraServiceLineModuleKey(moduleKey)
  const moduleName = extraServiceLineModuleLabel(normalisedModuleKey)
  const sendEndpoint = approvalEndpoint || (normalisedModuleKey === 'general'
    ? '/.netlify/functions/send-general-extra-service-line-approval'
    : '/.netlify/functions/send-suspension-extra-service-line-approval')
  const heading = title || 'Extra Service Lines'
  const serviceLabel = serviceSelectLabel || `Extra ${moduleName} Service`
  const jobId = job?.id
  const [rows, setRows] = useState([])
  const [evidenceRows, setEvidenceRows] = useState([])
  const [linkedEvidence, setLinkedEvidence] = useState({})
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState(() => makeEmptyForm())
  const [completionForms, setCompletionForms] = useState({})
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState('')
  const [sending, setSending] = useState('')
  const [missingSchema, setMissingSchema] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const vatInherited = Array.isArray(job?.service_snapshot) && job.service_snapshot.some(line => line.vat_source === true)
  const [vatPrices, setVatPrices] = useState(null)
  const vatSequence = useRef(0)
  useEffect(() => {vatSequence.current++;setVatPrices(null);setSaving(current=>current==='vat_review'?'':current);return()=>{vatSequence.current++}},[jobId])
  function clearVatReview() {vatSequence.current++;setVatPrices(null);setSaving(current=>current==='vat_review'?'':current)}
  async function reviewVatPrice() {
    const sequence=++vatSequence.current
    setVatPrices(null);setSaving('vat_review');setError('')
    try {
      const {data,error:err}=await supabase.rpc('workshop_vat_extra_prices',{p_job_id:jobId,p_service_id:form.service_id,p_customer_gross:form.line_amount===''?null:Number(form.line_amount)})
      if(err)throw new Error(err.message)
      if(!data?.tax?.complete)throw new Error('Reviewed extra service prices are unavailable.')
      if(sequence!==vatSequence.current)return
      setVatPrices(data);setForm(current=>({...current,line_amount:(data.tax.gross_minor/100).toFixed(2)}))
    }catch(err){if(sequence===vatSequence.current)setError(err.message)}finally{if(sequence===vatSequence.current)setSaving('')}
  }

  const moduleServices = useMemo(() => (
    (services || [])
      .filter(service => normaliseExtraServiceLineModuleKey(service?.module_key) === normalisedModuleKey)
      .filter(service => service.active !== false)
      .slice()
      .sort((a, b) => {
        const categorySort = serviceCategoryLabel(a).localeCompare(serviceCategoryLabel(b))
        if (categorySort !== 0) return categorySort
        return String(a.name || '').localeCompare(String(b.name || ''))
      })
  ), [services, normalisedModuleKey])

  const selectedService = moduleServices.find(service => service.id === form.service_id) || null
  const approvalRequiredLocked = Boolean(form.id && form.approval_required)

  const load = useCallback(async () => {
    if (!jobId || !workshopId) {
      setRows([])
      setEvidenceRows([])
      setLinkedEvidence({})
      if (onTotalsChange) onTotalsChange(0)
      if (onRowsChange) onRowsChange([])
      return
    }

    setLoading(true)
    setError('')
    try {
      const requestsResult = await supabase
        .from('job_extra_service_line_requests')
        .select('*')
        .eq('workshop_id', workshopId)
        .eq('job_id', jobId)
        .eq('module_key', normalisedModuleKey)
        .order('created_at', { ascending: false })

      if (requestsResult.error) throw requestsResult.error

      const linksResult = await supabase
        .from('job_extra_service_line_evidence')
        .select('id, service_line_request_id, evidence_id, active')
        .eq('workshop_id', workshopId)
        .eq('job_id', jobId)
        .eq('module_key', normalisedModuleKey)
        .eq('active', true)

      if (linksResult.error) throw linksResult.error

      const evidenceResult = await supabase
        .from('job_evidence')
        .select('id, file_name, caption, evidence_type, visibility, stage, checklist_item_id, created_at')
        .eq('workshop_id', workshopId)
        .eq('job_id', jobId)
        .order('created_at', { ascending: false })

      if (evidenceResult.error) throw evidenceResult.error

      const map = {}
      ;(linksResult.data || []).forEach(link => {
        const key = link.service_line_request_id
        if (!map[key]) map[key] = new Set()
        map[key].add(link.evidence_id)
      })

      const requestRows = requestsResult.data || []
      setRows(requestRows)
      setEvidenceRows(evidenceResult.data || [])
      setLinkedEvidence(map)
      setMissingSchema(false)
      if (onTotalsChange) onTotalsChange(extraServiceLineBillableTotal(requestRows))
      if (onRowsChange) onRowsChange(requestRows)
    } catch (err) {
      if (isMissingExtraServiceLineSchemaError(err)) {
        setMissingSchema(true)
        setRows([])
        setEvidenceRows([])
        setLinkedEvidence({})
        if (onTotalsChange) onTotalsChange(0)
        if (onRowsChange) onRowsChange([])
      } else {
        setError(err.message || 'Could not load extra service lines.')
      }
    } finally {
      setLoading(false)
    }
  }, [jobId, workshopId, normalisedModuleKey, onTotalsChange, onRowsChange])

  useEffect(() => {
    load()
  }, [load, refreshKey])

  useEffect(() => {
    if (!sourceDraft) return
    vatSequence.current++
    setVatPrices(null)
    setSaving(current=>current==='vat_review'?'':current)
    setEditing(true)
    setForm(makeEmptyForm(sourceDraft))
    setNotice('Extra service-line draft started from checklist item.')
    if (onSourceConsumed) onSourceConsumed()
  }, [sourceDraft, onSourceConsumed])

  function setField(key, value) {
    if(key==='line_amount')clearVatReview()
    setForm(current => ({ ...current, [key]: value }))
  }

  function changeService(serviceId) {
    clearVatReview()
    const service = moduleServices.find(row => row.id === serviceId)
    setForm(current => ({
      ...current,
      service_id: serviceId,
      line_amount: service && !vatInherited ? fieldAmount(service.base_price) : '',
    }))
  }

  function startNew() {
    clearVatReview()
    setEditing(true)
    setForm(makeEmptyForm())
    setError('')
    setNotice('')
  }

  function startEdit(row) {
    clearVatReview()
    setEditing(true)
    setForm(formFromRequest(row))
    setError('')
    setNotice('')
  }

  function cancelEdit() {
    clearVatReview()
    setEditing(false)
    setForm(makeEmptyForm())
    setError('')
  }

  function requestPayload() {
    return {
      workshop_id: workshopId,
      job_id: jobId,
      module_key: normalisedModuleKey,
      source_type: form.source_type || 'manual',
      checklist_item_id: form.checklist_item_id || null,
      finding_id: form.finding_id || null,
      service_id: form.service_id || null,
      service_snapshot: selectedService ? serviceSnapshot(selectedService, normalisedModuleKey) : {},
      customer_explanation: text(form.customer_explanation),
      internal_note: form.internal_note || '',
      line_amount: Number(form.line_amount || 0),
      approval_required: Boolean(form.approval_required),
      customer_visible: Boolean(form.customer_visible),
      status: form.status || 'draft',
      technician_completion_comment: form.technician_completion_comment || '',
    }
  }

  async function saveDraft() {
    if (!jobId) {
      setError(savedBeforeAddingMessage || `Save the ${moduleName} job before adding extra service lines.`)
      return
    }

    const payload = requestPayload()
    if(vatInherited){
      if(process.env.REACT_APP_VAT_BUILD3!=='true'||!vatPrices||vatPrices.service_id!==payload.service_id||vatPrices.tax.gross_minor!==Math.round(payload.line_amount*100)){
        setError('Review the extra-service customer total before saving.');return
      }
      payload.vat_price_snapshot=vatPrices
    }
    if (!payload.service_id) {
      setError(`Choose the extra ${moduleName} service.`)
      return
    }
    if (payload.line_amount <= 0) {
      setError('Add a price before saving this extra service line.')
      return
    }
    if (payload.status === 'awaiting_approval' && !payload.customer_explanation) {
      setError('Add a customer-facing explanation before sending approval.')
      return
    }

    setSaving('draft')
    setError('')
    setNotice('')
    try {
      const result = form.id
        ? await supabase
          .from('job_extra_service_line_requests')
          .update(payload)
          .eq('id', form.id)
          .eq('workshop_id', workshopId)
          .eq('job_id', jobId)
          .select('*')
          .single()
        : await supabase
          .from('job_extra_service_line_requests')
          .insert(payload)
          .select('*')
          .single()

      if (result.error) throw result.error
      await linkChecklistEvidence(result.data)
      setEditing(false)
      setForm(makeEmptyForm())
      await load()
      setNotice('Extra service line saved.')
      if (onChanged) onChanged()
    } catch (err) {
      setError(err.message || 'Could not save extra service line.')
    } finally {
      setSaving('')
    }
  }

  async function linkChecklistEvidence(savedRequest) {
    if (!savedRequest?.id || savedRequest.source_type !== 'checklist_item' || !savedRequest.checklist_item_id) return
    const checklistEvidence = evidenceRows.filter(item => item.checklist_item_id === savedRequest.checklist_item_id)
    if (!checklistEvidence.length) return
    const payload = checklistEvidence.map(item => ({
      workshop_id: workshopId,
      job_id: jobId,
      module_key: normalisedModuleKey,
      service_line_request_id: savedRequest.id,
      evidence_id: item.id,
      active: true,
    }))
    const { error: linkError } = await supabase
      .from('job_extra_service_line_evidence')
      .upsert(payload, { onConflict: 'service_line_request_id,evidence_id' })
    if (linkError) throw linkError
  }

  async function sendApproval(row) {
    if (!canSendExtraServiceLineApproval(row)) {
      setError('Choose a service, add customer-facing explanation and price before sending approval.')
      return
    }

    setSending(row.id)
    setError('')
    setNotice('')
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      if (!token) throw new Error('Please sign in again before sending approval.')

      const res = await fetch(sendEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          requestId: row.id,
          to: customerEmail,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'Could not send extra service approval.')
      await load()
      setNotice('Extra service approval link sent.')
      if (onChanged) onChanged()
    } catch (err) {
      setError(err.message || 'Could not send extra service approval.')
    } finally {
      setSending('')
    }
  }

  async function updateStatus(row, status) {
    if (['approved', 'declined', 'completed', 'not_completed'].includes(status)
      && row.customer_visible !== false
      && !text(row.customer_explanation)) {
      setError('Add a customer-facing explanation before making this extra service customer-visible or billable.')
      return
    }
    const completionComment = completionForms[row.id] || ''
    if (['completed', 'not_completed'].includes(status) && !text(completionComment)) {
      setError('Add a technician completion comment before marking this extra service complete or not completed.')
      return
    }

    setSaving(row.id)
    setError('')
    setNotice('')
    try {
      const result = status === 'approved'
        ? await supabase.rpc('approve_no_approval_extra_service_line', { p_request_id: row.id })
        : await supabase.rpc('close_extra_service_line_request', {
          p_request_id: row.id,
          p_status: status,
          p_technician_completion_comment: completionComment,
        })
      if (result.error) throw result.error
      await load()
      setNotice(`Extra service marked ${extraServiceLineStatusLabel(status).toLowerCase()}.`)
      if (onChanged) onChanged()
    } catch (err) {
      setError(err.message || 'Could not update extra service status.')
    } finally {
      setSaving('')
    }
  }

  async function voidRequest(row) {
    setSaving(row.id)
    setError('')
    setNotice('')
    try {
      const { error: updateError } = await supabase
        .rpc('void_draft_extra_service_line_request', { p_request_id: row.id })
      if (updateError) throw updateError
      await load()
      setNotice('Extra service line voided.')
      if (onChanged) onChanged()
    } catch (err) {
      setError(err.message || 'Could not void extra service line.')
    } finally {
      setSaving('')
    }
  }

  async function toggleEvidence(row, evidence) {
    const linked = linkedEvidence[row.id]?.has(evidence.id)
    setSaving(`evidence-${row.id}-${evidence.id}`)
    setError('')
    try {
      if (linked) {
        const { error: updateError } = await supabase
          .from('job_extra_service_line_evidence')
          .update({ active: false })
          .eq('service_line_request_id', row.id)
          .eq('evidence_id', evidence.id)
          .eq('workshop_id', workshopId)
        if (updateError) throw updateError
      } else {
        const { error: upsertError } = await supabase
          .from('job_extra_service_line_evidence')
          .upsert({
            workshop_id: workshopId,
            job_id: jobId,
            module_key: normalisedModuleKey,
            service_line_request_id: row.id,
            evidence_id: evidence.id,
            active: true,
          }, { onConflict: 'service_line_request_id,evidence_id' })
        if (upsertError) throw upsertError
      }
      await load()
      if (onChanged) onChanged()
    } catch (err) {
      setError(err.message || 'Could not update evidence link.')
    } finally {
      setSaving('')
    }
  }

  if (!jobId) {
    return (
      <>
        <div className="sec">{heading}</div>
        <div className="job-modal-callout" style={{ marginBottom: 14 }}>
          <div className="fs12 text-muted">{savedBeforeAddingMessage || `Save the ${moduleName} job before adding extra service-line approvals.`}</div>
        </div>
      </>
    )
  }

  return (
    <>
      <div className="sec">{heading}</div>
      <div className="job-modal-callout" style={{ marginBottom: 14 }}>
        <div className="section-head compact" style={{ marginBottom: 10 }}>
          <div>
            <div className="job-modal-callout-title">{introTitle || 'Standard services found mid-job'}</div>
            <div className="text-muted fs12">{introText || 'Use this for menu services like Headset Service. Keep bespoke one-off work in Bespoke Additional Work.'}</div>
          </div>
          {editable && <button className="btn btn-ghost btn-sm" type="button" onClick={startNew}>+ Add Service</button>}
        </div>

        {error && <div className="alert alert-error" style={{ marginBottom: 10 }}>{error}</div>}
        {notice && <div className="alert alert-success" style={{ marginBottom: 10 }}>{notice}</div>}
        {missingSchema && <div className="alert alert-info">Extra service-line schema v87 has not been applied yet.</div>}
        {loading && <div className="fs12 text-muted">Loading extra service lines...</div>}

        {editing && editable && !missingSchema && (
          <div style={{ border: '1px solid var(--border)', borderRadius: 6, padding: 14, background: 'var(--black)', marginBottom: 12 }}>
            <div className="grid-2">
              <div className="field">
                <label>{serviceLabel}</label>
                <select value={form.service_id} onChange={e => changeService(e.target.value)}>
                  <option value="">Choose service...</option>
                  {moduleServices.map(service => (
                    <option key={service.id} value={service.id}>
                      {service.name} - {serviceCategoryLabel(service)} - {vatInherited?'Review customer total':servicePriceLabel(service)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>{vatInherited?'Customer total including VAT (£)':'Service Amount (£)'}</label>
                <input type="number" min="0" step="0.01" value={form.line_amount} onChange={e => setField('line_amount', e.target.value)} placeholder="0.00" />
                {selectedService && <div className="field-hint">{vatInherited?'Leave blank to review the catalogue customer total, or enter an explicit customer gross.':`Defaults to ${servicePriceLabel(selectedService)} from the ${moduleName} service menu.`}</div>}
              </div>
            </div>

            {vatInherited&&<section aria-label="Extra service VAT review">
              <button type="button" className="btn btn-secondary btn-sm" disabled={Boolean(saving)||!form.service_id||process.env.REACT_APP_VAT_BUILD3!=='true'} onClick={reviewVatPrice}>Review extra service total</button>
              {vatPrices&&<>
                <div className="detail-row"><span>Subtotal (excluding VAT)</span><strong>{formatServiceMoney(vatPrices.tax.net_minor/100)}</strong></div>
                <div className="detail-row"><span>VAT</span><strong>{formatServiceMoney(vatPrices.tax.tax_minor/100)}</strong></div>
                <div className="detail-row"><span>Total</span><strong>{formatServiceMoney(vatPrices.tax.gross_minor/100)}</strong></div>
              </>}
            </section>}

            <div className="field">
              <label>Customer-facing explanation</label>
              <textarea
                value={form.customer_explanation}
                onChange={e => setField('customer_explanation', e.target.value)}
                placeholder="Explain why this standard service is recommended."
                style={{ minHeight: 82 }}
              />
              {extraServiceLineNeedsCustomerExplanation(form) && <div className="field-hint">Required before sending customer approval or showing this service in a customer report.</div>}
            </div>

            <div className="field">
              <label>Internal note</label>
              <textarea
                value={form.internal_note}
                onChange={e => setField('internal_note', e.target.value)}
                placeholder="Workshop-only note. This never appears in customer approval pages or reports."
                style={{ minHeight: 72 }}
              />
            </div>

            <div className="grid-3">
              <label className="check-row" style={{ alignSelf: 'center' }}>
                <input
                  type="checkbox"
                  checked={form.approval_required}
                  onChange={e => setField('approval_required', e.target.checked)}
                  disabled={approvalRequiredLocked}
                /> Approval required
              </label>
              <label className="check-row" style={{ alignSelf: 'center' }}>
                <input type="checkbox" checked={form.customer_visible} onChange={e => setField('customer_visible', e.target.checked)} /> Customer visible
              </label>
              <div className="detail-row">
                <span className="detail-label">Service-line amount</span>
                <span className="detail-val">{formatServiceMoney(extraServiceLinePrice(requestPayload()))}</span>
              </div>
            </div>
            {approvalRequiredLocked && (
              <div className="field-hint" style={{ marginTop: 8 }}>
                Saved approval-required service lines must use the customer approval link or be voided and recreated as no-approval.
              </div>
            )}

            <div className="action-row">
              <button className="btn btn-primary btn-sm" type="button" onClick={saveDraft} disabled={Boolean(saving)}>
                {saving === 'draft' ? 'Saving...' : form.id ? 'Save Service Line' : 'Create Service Line'}
              </button>
              <button className="btn btn-ghost btn-sm" type="button" onClick={cancelEdit} disabled={Boolean(saving)}>Cancel</button>
            </div>
          </div>
        )}

        {!loading && !rows.length && !editing && !missingSchema && (
          <div className="muted-empty">{emptyMessage || `No extra ${moduleName} service lines have been recorded yet.`}</div>
        )}

        <div style={{ display: 'grid', gap: 10 }}>
          {rows.map(row => {
            const statusTone = extraServiceLineStatusTone(row.status)
            const linked = linkedEvidence[row.id] || new Set()
            const canEdit = editable && canEditExtraServiceLine(row)
            const canSend = editable && canSendExtraServiceLineApproval(row)
            const canMarkComplete = editable && ['approved'].includes(row.status)
            const canMarkNoApproval = editable && row.approval_required === false && row.status === 'draft'
            return (
              <div key={row.id} className="muted-empty" style={{ textAlign: 'left', borderStyle: 'solid' }}>
                <div className="section-head compact">
                  <div>
                    <div className="text-white fw7">{serviceLineSnapshotName(row)}</div>
                    <div className="fs12 text-muted" style={{ marginTop: 3 }}>
                      {sourceLabel(row)} - {row.service_snapshot?.category_label || row.service_snapshot?.category || moduleName} - {formatServiceMoney(extraServiceLinePrice(row))}
                    </div>
                  </div>
                  <StatusPill tone={statusTone}>{extraServiceLineStatusLabel(row.status)}</StatusPill>
                </div>
                {row.customer_explanation && <div className="fs12" style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>{row.customer_explanation}</div>}
                {row.internal_note && editable && <div className="fs12 text-muted" style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>Internal: {row.internal_note}</div>}
                {row.customer_decision_at && (
                  <div className="fs12 text-muted" style={{ marginTop: 8 }}>
                    Customer responded {displayDateTime(row.customer_decision_at)}{row.customer_decision_note ? `: ${row.customer_decision_note}` : ''}
                  </div>
                )}
                {row.technician_completion_comment && (
                  <div className="fs12 text-muted" style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>
                    Completion: {row.technician_completion_comment}
                  </div>
                )}

                {editable && (
                  <>
                    <div className="action-row" style={{ justifyContent: 'flex-start', marginTop: 10 }}>
                      {canEdit && <button className="btn btn-ghost btn-sm" type="button" onClick={() => startEdit(row)}>Edit Draft</button>}
                      {canSend && <button className="btn btn-primary btn-sm" type="button" onClick={() => sendApproval(row)} disabled={sending === row.id || !customerEmail}>{sending === row.id ? 'Sending...' : 'Send Approval'}</button>}
                      {canMarkNoApproval && <button className="btn btn-ghost btn-sm" type="button" onClick={() => updateStatus(row, 'approved')} disabled={saving === row.id}>Approve Internally</button>}
                      {canMarkComplete && <button className="btn btn-ghost btn-sm" type="button" onClick={() => updateStatus(row, 'completed')} disabled={saving === row.id}>Mark Completed</button>}
                      {canMarkComplete && <button className="btn btn-ghost btn-sm" type="button" onClick={() => updateStatus(row, 'not_completed')} disabled={saving === row.id}>Not Completed</button>}
                      {row.status === 'draft' && <button className="btn btn-danger btn-sm" type="button" onClick={() => voidRequest(row)} disabled={saving === row.id}>Void</button>}
                    </div>
                    {(canMarkComplete || row.status === 'approved') && (
                      <div className="field" style={{ marginTop: 10, marginBottom: 0 }}>
                        <label>Technician completion comment</label>
                        <input
                          value={completionForms[row.id] || ''}
                          onChange={e => setCompletionForms(current => ({ ...current, [row.id]: e.target.value }))}
                          placeholder="Required before marking completed or not completed."
                        />
                      </div>
                    )}
                    {evidenceRows.length > 0 && (
                      <details style={{ marginTop: 10 }}>
                        <summary className="fs12 text-muted">Linked evidence ({linked.size})</summary>
                        <div style={{ display: 'grid', gap: 6, marginTop: 8 }}>
                          {evidenceRows.map(evidence => (
                            <label key={evidence.id} className="check-row" style={{ justifyContent: 'space-between' }}>
                              <span>
                                <input
                                  type="checkbox"
                                  checked={linked.has(evidence.id)}
                                  onChange={() => toggleEvidence(row, evidence)}
                                  disabled={saving === `evidence-${row.id}-${evidence.id}`}
                                /> {evidenceTitle(evidence)}
                              </span>
                              <span className={`job-status-chip job-status-${evidence.visibility === 'customer' ? 'green' : 'muted'}`}>
                                {evidence.visibility === 'customer' ? 'Customer report' : 'Internal only'}
                              </span>
                            </label>
                          ))}
                        </div>
                      </details>
                    )}
                  </>
                )}
              </div>
            )
          })}
        </div>

        {rows.length > 0 && (
          <div className="detail-row" style={{ marginTop: 12 }}>
            <span className="detail-label">Billable extra service lines</span>
            <span className="detail-val">{formatServiceMoney(extraServiceLineBillableTotal(rows))}</span>
          </div>
        )}
      </div>
    </>
  )
}
